<div class="bai-page-shell">

  <!-- Opportunities Table Card -->
  <div class="bai-surface-card">
    <div class="bai-section-header">
      <span class="bai-section-title">Opportunities</span>
      <p-button
        icon="pi pi-plus"
        [text]="true"
        size="small"
        (onClick)="openAddOpportunity()"
        aria-label="Add opportunity"
      />
    </div>

    <!-- Status filter chips -->
    <div class="bai-actions-filter-row" role="group" aria-label="Filter opportunities by status">
      <bai-filter-chip-group
        [options]="opportunityFilterOptions"
        [selected]="opportunityFilter()"
        [multiSelect]="true"
        ariaLabel="Filter opportunities by status"
        (selectionChange)="setOpportunityFilter($event)"
      />
    </div>

    <div class="overflow-x-auto">
      @if (filteredOpportunities().length === 0) {
        <p class="text-sm text-gray-400 px-4 py-4">No opportunities match the selected filters.</p>
      } @else {
        <table class="bai-actions-table bai-opp-table text-sm">
          <thead>
            <tr>
              <th baiColResize scope="col">Description</th>
              <th baiColResize scope="col">Source</th>
              <th baiColResize scope="col" class="bai-cell-nowrap">Source Type</th>
              <th baiColResize scope="col" class="bai-cell-nowrap">Created</th>
              <th baiColResize scope="col" class="bai-cell-nowrap">Updated</th>
              <th baiColResize scope="col" class="bai-cell-nowrap">Priority</th>
              <th baiColResize scope="col" class="bai-cell-nowrap">Status</th>
              <th scope="col" class="w-24 bai-cell-nowrap">Confirm</th>
              <th scope="col" class="w-24 bai-cell-nowrap">More</th>
            </tr>
          </thead>
          <tbody>
            @for (row of filteredOpportunities(); track row.id) {
              <tr
                class="cursor-pointer hover:bg-gray-50"
                (click)="openOpportunityDetails(row)"
              >
                <td [title]="row.opportunityDescription">
                  {{ row.opportunityDescription }}
                </td>
                <td>{{ row.source }}</td>
                <td class="bai-cell-nowrap">
                  <p-tag
                    [value]="row.sourceType === 'aiAgent' ? 'AI Agent' : 'Human'"
                    [severity]="row.sourceType === 'aiAgent' ? 'info' : 'secondary'"
                    styleClass="text-xs"
                  />
                </td>
                <td class="bai-cell-nowrap">{{ row.createdDate }}</td>
                <td class="bai-cell-nowrap">{{ row.updatedDate }}</td>
                <td class="bai-cell-nowrap">
                  <p-tag
                    [value]="row.priority"
                    [severity]="prioritySeverity(row.priority)"
                    styleClass="text-xs"
                  />
                </td>
                <td class="bai-cell-nowrap">
                  <p-tag
                    [value]="row.status"
                    [severity]="statusSeverity(row.status)"
                    styleClass="text-xs"
                  />
                </td>
                <td (click)="$event.stopPropagation(); $any($event.target).closest('[class*=__action]') && $any($event.currentTarget).removeAttribute('open')">
                  @if (row.sourceType === 'aiAgent' && row.status !== 'Withdrawn' && row.status !== 'Completed') {
                    <div class="flex gap-1" role="group" [attr.aria-label]="'Confirm actions for ' + row.opportunityDescription">
                      <p-button
                        icon="pi pi-check"
                        severity="success"
                        [text]="true"
                        size="small"
                        (onClick)="onApproveOpportunity(row.id)"
                        [attr.aria-label]="'Approve opportunity: ' + row.opportunityDescription"
                      />
                      <p-button
                        icon="pi pi-ban"
                        severity="danger"
                        [text]="true"
                        size="small"
                        (onClick)="onWithdrawOpportunity(row.id)"
                        [attr.aria-label]="'Withdraw opportunity: ' + row.opportunityDescription"
                      />
                    </div>
                  }
                </td>
                <td (click)="$event.stopPropagation(); $any($event.target).closest('[class*=__action]') && $any($event.currentTarget).removeAttribute('open')">
                  <details class="bai-copilot-kebab" (click)="$event.stopPropagation(); $any($event.target).closest('[class*=__action]') && $any($event.currentTarget).removeAttribute('open')">
                    <summary class="bai-copilot-kebab__btn" aria-label="Opportunity options">
                      <i class="pi pi-bars" aria-hidden="true"></i>
                    </summary>
                    <button
                      type="button"
                      class="bai-copilot-kebab__action"
                      (click)="addOpportunityToCopilot(row)"
                    >
                      Add to Copilot
                    </button>
                  </details>
                </td>
              </tr>
            }
          </tbody>
        </table>
      }
    </div>
  </div>

  <!-- Actions Table Card -->
  <div class="bai-surface-card">
    <div class="bai-section-header">
      <span class="bai-section-title">Client Actions</span>
      <p-button
        icon="pi pi-plus"
        [text]="true"
        size="small"
        (onClick)="openAddClientAction()"
        aria-label="Add action"
      />
    </div>

    <!-- Status filter chips -->
    <div class="bai-actions-filter-row" role="group" aria-label="Filter actions by status">
      <bai-filter-chip-group
        [options]="actionFilterOptions"
        [selected]="actionFilter()"
        [multiSelect]="true"
        ariaLabel="Filter actions by status"
        (selectionChange)="setActionFilter($event)"
      />
    </div>

    <div class="overflow-x-auto">
      @if (filteredActions().length === 0) {
        <p class="text-sm text-gray-400 px-4 py-4">No actions match the selected filters.</p>
      } @else {
        <table class="bai-actions-table bai-ca-table text-sm">
          <thead>
            <tr>
              <th baiColResize scope="col">FA Name</th>
              <th baiColResize scope="col">Action</th>
              <th baiColResize scope="col">Opportunity</th>
              <th baiColResize scope="col">Source</th>
              <th baiColResize scope="col" class="bai-cell-nowrap">Source Type</th>
              <th baiColResize scope="col" class="bai-cell-nowrap">Created</th>
              <th baiColResize scope="col" class="bai-cell-nowrap">Planned</th>
              <th baiColResize scope="col" class="bai-cell-nowrap">Deadline</th>
              <th baiColResize scope="col" class="bai-cell-nowrap">Status</th>
              <th scope="col" class="w-24 bai-cell-nowrap">Confirm</th>
              <th scope="col" class="w-24 bai-cell-nowrap">More</th>
            </tr>
          </thead>
          <tbody>
            @for (row of filteredActions(); track row.id) {
              <tr
                class="cursor-pointer hover:bg-gray-50"
                (click)="openClientActionDetails(row)"
              >
                <td>{{ row.financialAssistantName }}</td>
                <td [title]="row.action">{{ row.action }}</td>
                <td [title]="opportunityLabel(row.opportunityId)">
                  {{ opportunityLabel(row.opportunityId) }}
                </td>
                <td>{{ row.source }}</td>
                <td class="bai-cell-nowrap">
                  <p-tag
                    [value]="row.sourceType === 'aiAgent' ? 'AI Agent' : 'Human'"
                    [severity]="row.sourceType === 'aiAgent' ? 'info' : 'secondary'"
                    styleClass="text-xs"
                  />
                </td>
                <td class="bai-cell-nowrap">{{ row.createdDate }}</td>
                <td class="bai-cell-nowrap">{{ row.plannedDate }}</td>
                <td class="bai-cell-nowrap">{{ row.deadline }}</td>
                <td class="bai-cell-nowrap">
                  <p-tag
                    [value]="row.status"
                    [severity]="statusSeverity(row.status)"
                    styleClass="text-xs"
                  />
                </td>
                <td (click)="$event.stopPropagation(); $any($event.target).closest('[class*=__action]') && $any($event.currentTarget).removeAttribute('open')">
                  @if (row.sourceType === 'aiAgent' && row.status !== 'Withdrawn' && row.status !== 'Done') {
                    <div class="flex gap-1" role="group" [attr.aria-label]="'Confirm actions for ' + row.action">
                      <p-button
                        icon="pi pi-check"
                        severity="success"
                        [text]="true"
                        size="small"
                        (onClick)="onApproveAction(row.id)"
                        [attr.aria-label]="'Approve action: ' + row.action"
                      />
                      <p-button
                        icon="pi pi-ban"
                        severity="danger"
                        [text]="true"
                        size="small"
                        (onClick)="onWithdrawAction(row.id)"
                        [attr.aria-label]="'Withdraw action: ' + row.action"
                      />
                    </div>
                  }
                </td>
                <td (click)="$event.stopPropagation(); $any($event.target).closest('[class*=__action]') && $any($event.currentTarget).removeAttribute('open')">
                  <details class="bai-copilot-kebab" (click)="$event.stopPropagation(); $any($event.target).closest('[class*=__action]') && $any($event.currentTarget).removeAttribute('open')">
                    <summary class="bai-copilot-kebab__btn" aria-label="Client action options">
                      <i class="pi pi-bars" aria-hidden="true"></i>
                    </summary>
                    <button
                      type="button"
                      class="bai-copilot-kebab__action"
                      (click)="addActionToCopilot(row)"
                    >
                      Add to Copilot
                    </button>
                  </details>
                </td>
              </tr>
            }
          </tbody>
        </table>
      }
    </div>
  </div>

  <!-- FA Tasks Table Card -->
  <bai-fa-tasks [opportunities]="state().opportunities" />

  <!-- Outcomes Table Card -->
  <bai-outcomes [sessionId]="sessionId()" />

</div>
